import { QueryKeys } from "@/constants/Query";
import { type ApiError, useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import {
  constants,
  type CodeableConcept,
  Condition,
} from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type * as fhirTypes from "fhir/r4";
import type { MedicalHistory } from "./types";
import { withMultipleEncountersHandler } from "./useMedicalHistoryErrorHandler";

interface GetMedicalHistoryResponse {
  results: MedicalHistory[];
}

export const useGetMedicalHistory = ({
  patientId,
  enabled = true,
}: {
  patientId: string;
  enabled?: boolean;
}) => {
  return useApi({
    queryOptions: {
      queryKey:
        QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(patientId),
      queryFn: () =>
        axiosInstance.get<GetMedicalHistoryResponse>(
          "/charting/medical-history/episode-of-care",
          {
            params: {
              patientId,
            },
          },
        ),
      select: (response) => response.data,
      enabled: Boolean(patientId) && enabled,
    },
  });
};

export interface RawProblemListItemResponse {
  condition: fhirTypes.Condition;
  needsConfirmation: boolean;
  availableActions: {
    action: string;
    unavailableReasons?: string[];
  }[];
}

export interface RawProblemListResponse {
  results: RawProblemListItemResponse[];
}

export interface ProblemListItemResponse
  extends Omit<RawProblemListItemResponse, "condition"> {
  condition: Condition;
}

export interface ProblemListResponse {
  results: ProblemListItemResponse[];
}

export const selectProblemList = (
  response: RawProblemListResponse,
): ProblemListResponse => ({
  results: response.results.map((item) => ({
    ...item,
    condition: new Condition(item.condition),
  })),
});

export const useGetProblemList = ({
  patientId,
  enabled = true,
}: {
  patientId: string;
  enabled?: boolean;
}) =>
  useApi<RawProblemListResponse, ProblemListResponse>({
    queryOptions: {
      queryKey:
        QueryKeys.Patient.MedicalRecords.MedicalHistory.PROBLEM_LIST(patientId),
      queryFn: async () => {
        const response = await axiosInstance.get<RawProblemListResponse>(
          "/charting/medical-history/conditions/problem-list",
          { params: { patientId } },
        );
        return response.data;
      },
      select: selectProblemList,
      enabled: Boolean(patientId) && enabled,
    },
  });

export const useResolveMedicalHistory = ({
  patientId,
  episodeOfCareId,
  onSuccess,
  onError,
}: {
  patientId: string;
  episodeOfCareId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: () =>
        axiosInstance.patch(
          `/charting/medical-history/episode-of-care/${episodeOfCareId}/resolve`,
        ),
      mutationKey: QueryKeys.Patient.MedicalRecords.MedicalHistory.RESOLVE(
        patientId,
        episodeOfCareId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MedicalHistory.PROBLEM_LIST(
              patientId,
            ),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Conditions.DEFAULT(patientId),
        });
        onSuccess?.();
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

export const useVoidMedicalHistory = ({
  patientId,
  episodeOfCareId,
  onSuccess,
  onError,
}: {
  patientId: string;
  episodeOfCareId: string;
  onSuccess?: () => void;
  onError?: () => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<void, void>({
    mutationOptions: {
      mutationFn: () =>
        axiosInstance.delete(
          `/charting/medical-history/episode-of-care/${episodeOfCareId}/void`,
        ),
      mutationKey: QueryKeys.Patient.MedicalRecords.MedicalHistory.VOID(
        patientId,
        episodeOfCareId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MedicalHistory.PROBLEM_LIST(
              patientId,
            ),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Conditions.DEFAULT(patientId),
        });
        onSuccess?.();
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

export const useReactivateMedicalHistory = ({
  patientId,
  episodeOfCareId,
  onSuccess,
  onError,
}: {
  patientId: string;
  episodeOfCareId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: () =>
        axiosInstance.patch(
          `/charting/medical-history/episode-of-care/${episodeOfCareId}/reactivate`,
        ),
      mutationKey: QueryKeys.Patient.MedicalRecords.MedicalHistory.REACTIVATE(
        patientId,
        episodeOfCareId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Conditions.DEFAULT(patientId),
        });
        onSuccess?.();
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

export const useConfirmMedicalHistory = ({
  patientId,
  episodeOfCareId,
  onSuccess,
  onError,
}: {
  patientId: string;
  episodeOfCareId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: () =>
        axiosInstance.patch(
          `/charting/medical-history/episode-of-care/${episodeOfCareId}/confirm`,
        ),
      mutationKey: QueryKeys.Patient.MedicalRecords.MedicalHistory.CONFIRM(
        patientId,
        episodeOfCareId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Conditions.DEFAULT(patientId),
        });
        onSuccess?.();
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

export const useConfirmMultipleMedicalHistory = ({
  patientId,
  episodeOfCareIds,
  onSuccess,
  onError,
}: {
  patientId: string;
  episodeOfCareIds: string[];
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: () =>
        axiosInstance.post(
          "/charting/medical-history/episode-of-care/confirm-multiple",
          { episodeOfCareIds },
        ),
      mutationKey:
        QueryKeys.Patient.MedicalRecords.MedicalHistory.CONFIRM_MULTIPLE(
          patientId,
          episodeOfCareIds,
        ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Conditions.DEFAULT(patientId),
        });
        onSuccess?.();
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

interface UpdateMedicalHistoryRequest {
  conditionCode: Pick<CodeableConcept, "coding" | "text">;
}

interface ConditionResponse {
  condition: Condition;
}

interface ConditionsResponse {
  conditions: Condition[];
}

interface UpdateConditionNoteRequest {
  note: string | null;
}

interface ConfirmMultipleConditionsRequest {
  conditionIds: string[];
}

const invalidateConditionQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  patientId: string,
) => {
  queryClient.invalidateQueries({
    queryKey:
      QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(patientId),
  });
  queryClient.invalidateQueries({
    queryKey:
      QueryKeys.Patient.MedicalRecords.MedicalHistory.PROBLEM_LIST(patientId),
  });
  queryClient.invalidateQueries({
    queryKey: QueryKeys.Patient.Chart.Conditions.DEFAULT(patientId),
  });
  queryClient.invalidateQueries({
    queryKey: QueryKeys.Patient.MedicalRecords.Conditions.Search.DEFAULT({
      patientId,
    }),
  });
};

export const useResolveCondition = ({
  patientId,
  conditionId,
  onSuccess,
  onError,
}: {
  patientId: string;
  conditionId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<ConditionResponse, void>({
    mutationOptions: {
      mutationFn: async () => {
        const response = await axiosInstance.patch<ConditionResponse>(
          `/charting/medical-history/condition/${conditionId}/resolve`,
        );
        return response.data;
      },
      mutationKey:
        QueryKeys.Patient.MedicalRecords.MedicalHistory.CONDITION_RESOLVE(
          patientId,
          conditionId,
        ),
      onSuccess: () => {
        invalidateConditionQueries(queryClient, patientId);
        onSuccess?.();
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

export const useActivateCondition = ({
  patientId,
  conditionId,
  onSuccess,
  onError,
}: {
  patientId: string;
  conditionId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<ConditionResponse, void>({
    mutationOptions: {
      mutationFn: async () => {
        const response = await axiosInstance.patch<ConditionResponse>(
          `/charting/medical-history/condition/${conditionId}/activate`,
        );
        return response.data;
      },
      mutationKey:
        QueryKeys.Patient.MedicalRecords.MedicalHistory.CONDITION_ACTIVATE(
          patientId,
          conditionId,
        ),
      onSuccess: () => {
        invalidateConditionQueries(queryClient, patientId);
        onSuccess?.();
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

export const useConfirmCondition = ({
  patientId,
  conditionId,
  onSuccess,
  onError,
}: {
  patientId: string;
  conditionId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<ConditionResponse, void>({
    mutationOptions: {
      mutationFn: async () => {
        const response = await axiosInstance.patch<ConditionResponse>(
          `/charting/medical-history/condition/${conditionId}/confirm`,
        );
        return response.data;
      },
      mutationKey:
        QueryKeys.Patient.MedicalRecords.MedicalHistory.CONDITION_CONFIRM(
          patientId,
          conditionId,
        ),
      onSuccess: () => {
        invalidateConditionQueries(queryClient, patientId);
        onSuccess?.();
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

export const useVoidCondition = ({
  patientId,
  conditionId,
  onSuccess,
  onError,
}: {
  patientId: string;
  conditionId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<ConditionResponse, void>({
    mutationOptions: {
      mutationFn: async () => {
        const response = await axiosInstance.delete<ConditionResponse>(
          `/charting/medical-history/condition/${conditionId}/void`,
        );
        return response.data;
      },
      mutationKey:
        QueryKeys.Patient.MedicalRecords.MedicalHistory.CONDITION_VOID(
          patientId,
          conditionId,
        ),
      onSuccess: () => {
        invalidateConditionQueries(queryClient, patientId);
        onSuccess?.();
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

export const useConfirmMultipleConditions = ({
  patientId,
  onSuccess,
  onError,
}: {
  patientId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<ConditionsResponse, ConfirmMultipleConditionsRequest>({
    mutationOptions: {
      mutationFn: async (data) => {
        const response = await axiosInstance.post<ConditionsResponse>(
          "/charting/medical-history/condition/confirm-multiple",
          data,
        );
        return response.data;
      },
      mutationKey:
        QueryKeys.Patient.MedicalRecords.MedicalHistory.CONDITION_CONFIRM_MULTIPLE(
          patientId,
        ),
      onSuccess: () => {
        invalidateConditionQueries(queryClient, patientId);
        onSuccess?.();
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

export const useUpdateConditionNote = ({
  patientId,
  conditionId,
  onSuccess,
  onError,
}: {
  patientId: string;
  conditionId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<ConditionResponse, UpdateConditionNoteRequest>({
    mutationOptions: {
      mutationFn: async (data) => {
        const response = await axiosInstance.patch<ConditionResponse>(
          `/charting/medical-history/condition/${conditionId}/note`,
          data,
        );
        return response.data;
      },
      mutationKey:
        QueryKeys.Patient.MedicalRecords.MedicalHistory.CONDITION_NOTE(
          patientId,
          conditionId,
        ),
      onSuccess: () => {
        invalidateConditionQueries(queryClient, patientId);
        onSuccess?.();
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

export const useUpdateMedicalHistory = ({
  patientId,
  episodeOfCareId,
  onSuccess,
  onError,
}: {
  patientId: string;
  episodeOfCareId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: (data: UpdateMedicalHistoryRequest) =>
        axiosInstance.patch(
          `/charting/medical-history/episode-of-care/${episodeOfCareId}`,
          data,
        ),
      mutationKey: QueryKeys.Patient.MedicalRecords.MedicalHistory.UPDATE(
        patientId,
        episodeOfCareId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MedicalHistory.PROBLEM_LIST(
              patientId,
            ),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Conditions.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.DEFAULT({
            patientId,
          }),
        });

        // Invalidate the conditions search query since updating the condition code will modify
        // what conditions are available to be charted on the patient
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Conditions.Search.DEFAULT({
            patientId,
          }),
        });
        onSuccess?.();
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

interface UpdateEncounterEpisodeOfCareRequest {
  conditionCode: Pick<CodeableConcept, "coding" | "text">;
}

export const useUpdateEncounterEpisodeOfCare = ({
  patientId,
  motherEncounterId,
  episodeOfCareId,
  onSuccess,
  onError,
}: {
  patientId: string;
  motherEncounterId: string;
  episodeOfCareId: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: (data: UpdateEncounterEpisodeOfCareRequest) =>
        axiosInstance.patch(
          `/charting/encounter-charting/episode-of-care/${episodeOfCareId}`,
          data,
        ),
      mutationKey: QueryKeys.Patient.Chart.EpisodesOfCare.DEFAULT({
        patientId,
        encounterId: motherEncounterId,
      }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Conditions.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.DEFAULT({
            patientId,
            encounterId: motherEncounterId,
          }),
        });
        // Invalidate Rx Recommendations query when EOC title is updated
        // The protocol suggestion API depends on the EOC condition code
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.RxRecommendations.EOC({
            patientId,
            eocId: episodeOfCareId,
          }),
        });
        onSuccess?.();
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

interface BatchAddMedicalHistoryRequest {
  patientId: string;
  conditionCodes: Pick<CodeableConcept, "coding" | "text">[];
}

interface BatchAddEpisodesOfCareResponse {
  successfulEocIds: string[];
  errors: unknown[];
}

interface BatchAddProblemListConditionsResponse {
  conditions: unknown[];
  errors: unknown[];
}

interface BatchAddMedicalHistoryResult {
  addedCount: number;
  failedCount: number;
}

/**
 * Adds medical history items in one request. With single-encounter visits on,
 * each code becomes an EpisodeOfCare-less problem list Condition, de-duplicated
 * against the same problem list the medical history shows; otherwise each code
 * gets a legacy EpisodeOfCare. Either way, items that could not be added are
 * counted in `failedCount` rather than failing the request.
 */
export const useBatchAddMedicalHistory = ({
  patientId,
  onSuccess,
  onError,
}: {
  patientId: string;
  onSuccess?: (result: BatchAddMedicalHistoryResult) => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  const { data: isSingleEncounterVisitsEnabled } = useConfigCatFeatureFlag(
    "single-encounter-visits",
  );
  return useApiMutation({
    mutationOptions: {
      mutationFn: async (
        data: BatchAddMedicalHistoryRequest,
      ): Promise<BatchAddMedicalHistoryResult> => {
        if (isSingleEncounterVisitsEnabled === true) {
          const response =
            await axiosInstance.post<BatchAddProblemListConditionsResponse>(
              "/charting/medical-history/condition/batch-add",
              {
                patientId: data.patientId,
                conditions: data.conditionCodes.map((conditionCode) => {
                  const snomedCoding = conditionCode.coding?.find(
                    (coding) => coding.system === constants.SNOMED_SYSTEM_URL,
                  );
                  const display =
                    snomedCoding?.display ?? conditionCode.text ?? "";
                  return {
                    conditionSnomedCode: snomedCoding?.code ?? "",
                    conditionDisplay: display,
                  };
                }),
              },
            );
          return {
            addedCount: response.data.conditions.length,
            failedCount: response.data.errors.length,
          };
        }

        const response =
          await axiosInstance.post<BatchAddEpisodesOfCareResponse>(
            "/charting/medical-history/episode-of-care/batch-add",
            data,
          );
        return {
          addedCount: response.data.successfulEocIds.length,
          failedCount: response.data.errors.length,
        };
      },
      mutationKey:
        QueryKeys.Patient.MedicalRecords.MedicalHistory.BATCH_CREATE(patientId),
      onSuccess: (result) => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MedicalHistory.PROBLEM_LIST(
              patientId,
            ),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Conditions.DEFAULT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Conditions.Search.DEFAULT({
            patientId,
          }),
        });
        onSuccess?.(result);
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

// =============================================================================
// AV Call Suggested Conditions
// =============================================================================

export interface AVCallSuggestedSnomedCode {
  code: string | null;
  display: string | null;
  system: string | null;
  addedToChartAt?: string | null;
}

export interface AVCallSuggestedCondition {
  term: string | null;
  snomedCodes: AVCallSuggestedSnomedCode[];
  sourceId?: string | null;
}

export interface AVCallSuggestedConditionsData {
  encounterId: string;
  btSpanId?: string | null;
  historicalConditions: AVCallSuggestedCondition[];
  rfeConditions: AVCallSuggestedCondition[];
  markedAsErroneousAt?: string | null;
}

/**
 * Fetch AV-call-generated condition suggestions for the current encounter.
 *
 * On a 404 (no extraction row exists yet — workflow hasn't completed, or
 * this wasn't an AV call) we resolve with an empty-shape default so the
 * UI can render the empty state without a separate null branch and without
 * tripping React Query's "queryFn returned null" warning in newer versions.
 */
export const useAVCallSuggestedConditions = ({
  patientId,
  encounterId,
  enabled = true,
}: {
  patientId?: string;
  encounterId?: string;
  enabled?: boolean;
}) =>
  useApi<AVCallSuggestedConditionsData>({
    queryOptions: {
      queryKey: QueryKeys.Patient.Chart.AVCallConditions.DEFAULT({
        patientId,
        encounterId,
      }),
      queryFn: async () => {
        try {
          const response =
            await axiosInstance.get<AVCallSuggestedConditionsData>(
              "/charting/ai-charting/av-call-conditions",
              { params: { encounterId } },
            );
          return response.data;
        } catch (e) {
          if (axios.isAxiosError(e) && e.response?.status === 404) {
            return {
              encounterId: encounterId ?? "",
              historicalConditions: [],
              rfeConditions: [],
            };
          }
          throw e;
        }
      },
      enabled: enabled && !!encounterId,
    },
  });

interface MarkAVCallConditionAddedToChartRequest {
  encounterId: string;
  conditionCode: string;
  conditionCodeSystem: string;
  date?: string;
}

/**
 * Mark a specific SNOMED code suggestion on an AV-call extraction record as
 * added to the patient's chart. Best-effort — failures should be logged but
 * not block the EOC that's already been created on the chart.
 */
export const useMarkAVCallConditionAddedToChart = ({
  patientId,
  encounterId,
}: {
  patientId: string;
  encounterId?: string;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<void, MarkAVCallConditionAddedToChartRequest>({
    mutationOptions: {
      mutationKey:
        QueryKeys.Patient.Chart.AVCallConditions.MARK_CONDITION_ADDED_TO_CHART(
          patientId,
        ),
      mutationFn: async (payload: MarkAVCallConditionAddedToChartRequest) => {
        await axiosInstance.put(
          `/charting/ai-charting/av-call-conditions/${payload.encounterId}/condition-added-to-chart`,
          {
            conditionCode: payload.conditionCode,
            conditionCodeSystem: payload.conditionCodeSystem,
            date: payload.date,
          },
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.AVCallConditions.DEFAULT({
            patientId,
            encounterId,
          }),
        });
      },
    },
  });
};

/**
 * Number of AV-call historical-condition suggestions that the provider hasn't
 * actioned yet — i.e. extracted conditions where none of the candidate SNOMED
 * codes have an `addedToChartAt` timestamp set. Returns 0 if the record was
 * marked-as-erroneous (the provider explicitly dismissed everything).
 *
 * Shared by the section-header count pill and the collapsed-sidebar dot
 * indicator so both surfaces agree on what "un-actioned" means.
 */
export const getUnActionedAVCallHistoricalCount = (
  data: AVCallSuggestedConditionsData | undefined,
): number => {
  if (!data || data.markedAsErroneousAt) return 0;
  return (data.historicalConditions ?? []).filter((c) =>
    (c.snomedCodes ?? []).every((s) => !s.addedToChartAt),
  ).length;
};
